"use client"

import { useState } from "react"

export type AlertSubscribeStatus = "idle" | "submitting" | "success" | "error"

/**
 * Shared submit state for the alert opt-in forms. POSTs to
 * /api/alerts/subscribe; success means "pending confirmation" (double opt-in
 * — the confirmation email is composed server-side and sent via Resend when
 * configured; the user must click the link before any subscription activates).
 *
 * `subscribe` RESOLVES TO WHETHER THE SUBSCRIBE SUCCEEDED. Callers that only
 * need the rendered status keep ignoring it (`void subscribe(...)`); callers
 * with a success side effect need it, because `status` read straight after the
 * call is the stale closure value, not the new one. FRIDGE-279 uses it to mark
 * the popup-suppression key.
 */
export function useAlertSubscribe() {
  const [status, setStatus] = useState<AlertSubscribeStatus>("idle")
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  async function subscribe(payload: Record<string, unknown>): Promise<boolean> {
    setStatus("submitting")
    setErrorMessage(null)
    try {
      const res = await fetch("/api/alerts/subscribe", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(payload),
      })
      const data = await res.json().catch(() => null)
      if (res.ok && data?.ok) {
        setStatus("success")
        return true
      }
      setStatus("error")
      setErrorMessage(
        typeof data?.error === "string" && data.error.includes(" ")
          ? data.error
          : "Something went wrong. Please check your email address and try again.",
      )
      return false
    } catch {
      setStatus("error")
      setErrorMessage("Something went wrong. Please try again.")
      return false
    }
  }

  return { status, errorMessage, subscribe }
}
